/**
 * RepositoryFactory - Factory per creare e switchare repository
 *
 * Permette di cambiare implementazione DB senza modificare l'app
 * Supporta: Memory, LocalStorage, IndexedDB (futuro), WASM (futuro)
 */

import type { IDataRepository, RepositoryConfig } from './types';
import { MemoryRepository } from './MemoryRepository';
import { LocalStorageRepository } from './LocalStorageRepository';

export type RepositoryType =
  | 'memory'
  | 'localStorage'
  | 'indexedDB' // futuro
  | 'wasm'      // futuro
  | 'remote';   // futuro

export interface RepositoryFactoryConfig extends RepositoryConfig {
  /** Type of repository to create */
  type: RepositoryType;

  /** Storage key prefix (for localStorage/indexedDB) */
  storageKey?: string;

  /** Auto-init repository after creation */
  autoInit?: boolean;
}

/**
 * Global repository factory singleton
 */
export class RepositoryFactory {
  private static instance: RepositoryFactory;
  private currentRepository?: IDataRepository;
  private currentType?: RepositoryType;

  private constructor() {}

  static getInstance(): RepositoryFactory {
    if (!RepositoryFactory.instance) {
      RepositoryFactory.instance = new RepositoryFactory();
    }
    return RepositoryFactory.instance;
  }

  /**
   * Create repository instance
   */
  async create(config: RepositoryFactoryConfig): Promise<IDataRepository> {
    const { type, storageKey, autoInit = true, ...repoConfig } = config;

    let repository: IDataRepository;

    switch (type) {
      case 'memory':
        repository = new MemoryRepository();
        break;

      case 'localStorage':
        repository = new LocalStorageRepository(storageKey);
        break;

      case 'indexedDB':
        throw new Error('IndexedDB repository not implemented yet');

      case 'wasm':
        throw new Error('WASM repository not implemented yet');

      case 'remote':
        throw new Error('Remote repository not implemented yet');

      default:
        throw new Error(`Unknown repository type: ${type}`);
    }

    if (autoInit) {
      await repository.init(repoConfig);
    }

    // Store as current
    this.currentRepository = repository;
    this.currentType = type;

    return repository;
  }

  /**
   * Get current repository (creates default if not exists)
   */
  async getCurrent(): Promise<IDataRepository> {
    if (!this.currentRepository) {
      // Auto-create default repository
      await this.create({
        type: this.getDefaultType(),
        autoInit: true,
      });
    }
    return this.currentRepository!;
  }

  /**
   * Switch repository type (migrates data if requested)
   */
  async switch(
    newType: RepositoryType,
    options: {
      migrateData?: boolean;
      storageKey?: string;
    } = {}
  ): Promise<IDataRepository> {
    const { migrateData = false, storageKey } = options;

    // Create new repository
    const newRepo = await this.create({
      type: newType,
      storageKey,
      autoInit: true,
    });

    // Migrate data if requested
    if (migrateData && this.currentRepository) {
      await this.migrateData(this.currentRepository, newRepo);
    }

    return newRepo;
  }

  /**
   * Get current repository type
   */
  getCurrentType(): RepositoryType | undefined {
    return this.currentType;
  }

  /**
   * Clear current repository
   */
  async clear(): Promise<void> {
    if (this.currentRepository) {
      if ('clear' in this.currentRepository) {
        await (this.currentRepository as any).clear();
      }
    }
  }

  // =============================================================================
  // PRIVATE METHODS
  // =============================================================================

  /**
   * Get default repository type based on environment
   */
  private getDefaultType(): RepositoryType {
    // Check if in browser
    if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
      return 'localStorage';
    }

    // Fallback to memory
    return 'memory';
  }

  /**
   * Migrate data from one repository to another
   */
  private async migrateData(
    source: IDataRepository,
    target: IDataRepository
  ): Promise<void> {
    console.log('Migrating data between repositories...');

    // Query all records from source
    const allRecords = await source.query({});

    if (allRecords.length === 0) {
      console.log('No data to migrate');
      return;
    }

    // Batch insert into target
    const batchOps = allRecords.map((record) => ({
      type: 'put' as const,
      record,
    }));

    const result = await target.batch(batchOps);

    const successCount = result.results.filter((r) => r.success).length;
    console.log(`Migrated ${successCount}/${allRecords.length} records`);

    if (!result.success) {
      const errors = result.results.filter((r) => !r.success);
      console.warn('Migration errors:', errors);
    }
  }
}

/**
 * Helper function to get current repository
 */
export async function getRepository(): Promise<IDataRepository> {
  return RepositoryFactory.getInstance().getCurrent();
}

/**
 * Helper function to create repository
 */
export async function createRepository(
  config: RepositoryFactoryConfig
): Promise<IDataRepository> {
  return RepositoryFactory.getInstance().create(config);
}
